/**
 * Workflow Subscription Detail/Edit View
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Alert,
  Tooltip,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { ViewToolbar } from '@/components/ViewToolbar';
import { saveShortcutLabel, cancelShortcutLabel } from '@/hooks/useSaveShortcut';
import {
  useWorkflowSubscription,
  useCreateWorkflowSubscription,
  useUpdateWorkflowSubscription,
} from './hooks/useWorkflowChaining';
import { EventTypeSelector } from './components/EventTypeSelector';
import { WorkflowSelector } from './components/WorkflowSelector';
import type { CreateUpdateWorkflowSubscription, WorkflowSubscriptionMode } from './types';

export const WorkflowSubscriptionDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data: subscription, isLoading } = useWorkflowSubscription(isNew ? undefined : id);
  const createMutation = useCreateWorkflowSubscription();
  const updateMutation = useUpdateWorkflowSubscription();

  const { control, handleSubmit, reset, formState: { errors } } = useForm<CreateUpdateWorkflowSubscription>({
    defaultValues: {
      name: '',
      isDslDefined: false,
      eventType: '',
      workflowDefinitionId: '',
      mode: 0 as WorkflowSubscriptionMode,
      isEnabled: true,
      filterExpression: '',
      inputMappingJson: '',
      correlationIdTemplate: '',
    },
  });

  useEffect(() => {
    if (subscription) {
      reset({
        name: subscription.name || '',
        isDslDefined: subscription.isDslDefined || false,
        eventType: subscription.eventType,
        workflowDefinitionId: subscription.workflowDefinitionId,
        mode: subscription.mode,
        tenantId: subscription.tenantId,
        orgUnitScopeId: subscription.orgUnitScopeId,
        isEnabled: subscription.isEnabled,
        filterExpression: subscription.filterExpression || '',
        inputMappingJson: subscription.inputMappingJson || '',
        correlationIdTemplate: subscription.correlationIdTemplate || '',
        maxRetries: subscription.maxRetries,
        retryStrategy: subscription.retryStrategy || '',
      });
    }
  }, [subscription, reset]);

  const onSubmit = async (data: CreateUpdateWorkflowSubscription) => {
    try {
      setSaveError(null);
      // Clean up empty strings to null for optional JSON fields
      const cleanedData = {
        ...data,
        filterExpression: data.filterExpression?.trim() || undefined,
        inputMappingJson: data.inputMappingJson?.trim() || undefined,
        correlationIdTemplate: data.correlationIdTemplate?.trim() || undefined,
      };

      if (isNew) {
        await createMutation.mutateAsync(cleanedData);
      } else if (id) {
        await updateMutation.mutateAsync({ id, input: cleanedData });
      }
      navigate('/core/workflow-chaining');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setSaveError(`Failed to save subscription: ${errorMessage}`);
      console.error('Failed to save subscription:', error);
    }
  };

  const handleCancel = () => {
    navigate('/core/workflow-chaining');
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      {/* Toolbar with actions on left */}
      <ViewToolbar onBack={handleCancel} onSave={handleSubmit(onSubmit)} saveDisabled={isSaving} onCancel={handleCancel} cancelDisabled={isSaving}>
        <Tooltip title={cancelShortcutLabel()}>
          <span>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSaving}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                px: 2,
                ...(isSaving
                  ? { borderColor: 'grey.300', color: 'grey.400' }
                  : { borderColor: 'grey.400', color: 'text.secondary' }),
              }}
            >
              Cancel
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={saveShortcutLabel()}>
          <span>
            <Button
              variant="outlined"
              onClick={handleSubmit(onSubmit)}
              disabled={isSaving}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                px: 2,
                ...(isSaving
                  ? { borderColor: 'grey.300', color: 'grey.400' }
                  : { borderColor: 'primary.main', color: 'primary.main' }),
              }}
            >
              {isSaving ? 'Saving...' : (isNew ? 'Create' : 'Save')}
            </Button>
          </span>
        </Tooltip>
      </ViewToolbar>

      {/* Page content */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 500 }}>
          {isNew ? 'New Workflow Subscription' : 'Edit Workflow Subscription'}
        </Typography>

        {saveError && (
          <Alert severity="error" onClose={() => setSaveError(null)} sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}

        {subscription?.isDslDefined && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This subscription is defined in DSL files. Changes here may be overwritten on DSL sync.
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Name */}
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name (optional)"
                  placeholder="e.g., Order Approval on Approval Requested"
                  helperText="Descriptive name for this subscription"
                />
              )}
            />

            {/* Event Type */}
            <Controller
              name="eventType"
              control={control}
              rules={{ required: 'Event type is required' }}
              render={({ field }) => (
                <EventTypeSelector
                  value={field.value}
                  onChange={field.onChange}
                  error={!!errors.eventType}
                  helperText={errors.eventType?.message}
                />
              )}
            />

            {/* Workflow */}
            <Controller
              name="workflowDefinitionId"
              control={control}
              rules={{ required: 'Workflow is required' }}
              render={({ field }) => (
                <WorkflowSelector
                  value={field.value}
                  onChange={field.onChange}
                  error={!!errors.workflowDefinitionId}
                  helperText={errors.workflowDefinitionId?.message}
                />
              )}
            />

            {/* Enabled */}
            <Controller
              name="isEnabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={field.onChange} />}
                  label="Enabled"
                />
              )}
            />

            {/* Filter Expression */}
            <Controller
              name="filterExpression"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Filter Expression (optional)"
                  multiline
                  rows={3}
                  placeholder="e.g., order.TotalAmount > 1000"
                  helperText="JavaScript expression to filter events"
                />
              )}
            />

            {/* Input Mapping */}
            <Controller
              name="inputMappingJson"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Input Mapping JSON (optional)"
                  multiline
                  rows={4}
                  placeholder='{"workflowInput": "event.propertyName"}'
                  helperText="JSONPath mapping from event to workflow input"
                />
              )}
            />

            {/* Correlation ID Template */}
            <Controller
              name="correlationIdTemplate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Correlation ID Template (optional)"
                  placeholder="Order:{OrderId}"
                  helperText="Template for generating correlation IDs"
                />
              )}
            />
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

